import { Close, Favorite, Report } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
  Popover,
  TextField,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import type { ReportEntityType } from '@/types';

const entityLabels: Record<ReportEntityType, string> = {
  Unit: 'Lektion',
  Flashcard: 'Karteikarte',
  Question: 'Frage',
};

type ReportDialogProps = {
  open: boolean;
  onClose: () => void;
  onSubmit: (message: string) => Promise<boolean>;
  entityType: ReportEntityType;
  isLoading?: boolean;
  anchorEl?: HTMLElement | null;
  presentation?: 'dialog' | 'popover' | 'popover-below';
};

export default function ReportDialog({
  open,
  onClose,
  onSubmit,
  entityType,
  isLoading = false,
  anchorEl,
  presentation = 'popover',
}: ReportDialogProps) {
  const [report, setReport] = useState('');
  const [hasError, setHasError] = useState(false);
  const [hasSuccess, setHasSuccess] = useState(false);

  const handleSubmit = async () => {
    if (!report.trim()) return;

    setHasError(false);
    try {
      const success = await onSubmit(report.trim());
      if (success) {
        setHasSuccess(true);
      } else {
        setHasError(true);
      }
    } catch {
      setHasError(true);
    }
  };

  const handleClose = () => {
    onClose();
  };

  const handleExited = () => {
    setReport('');
    setHasError(false);
    setHasSuccess(false);
  };

  const entityLabel = entityLabels[entityType];

  const content = (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <Report fontSize="small" />
        <Typography variant="subtitle2">{entityLabel} melden</Typography>
        <IconButton onClick={handleClose} sx={{ ml: 'auto' }} size="small">
          <Close />
        </IconButton>
      </Box>

      {hasSuccess ? (
        <>
          <Alert severity="success" sx={{ fontSize: '0.875rem', mb: 2 }} icon={<Favorite />}>
            Vielen Dank für deine Unterstützung! Dein Hinweis hilft anderen beim Lernen.
          </Alert>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button onClick={handleClose} size="small" variant="contained">
              Schließen
            </Button>
          </Box>
        </>
      ) : (
        <>
          <Alert severity="info" sx={{ mb: 2, fontSize: '0.875rem' }}>
            Der angezeigte Inhalt weist einen Fehler auf?
          </Alert>

          <TextField
            fullWidth
            multiline
            rows={4}
            label="Weshalb meldest du diesen Inhalt?"
            placeholder="Beschreibe den Fehler und füge wenn möglich eine Korrektur hinzu..."
            value={report}
            onChange={(e) => setReport(e.target.value)}
            disabled={isLoading}
            variant="outlined"
            required
            size="small"
          />

          {hasError && (
            <Alert severity="error" sx={{ mt: 2, fontSize: '0.875rem' }}>
              Fehler beim Senden. Bitte versuche es erneut.
            </Alert>
          )}

          <Box
            sx={{
              display: 'flex',
              width: '100%',
              justifyContent: 'space-between',
              gap: 1,
              mt: 2,
            }}>
            <Button onClick={handleClose} disabled={isLoading} size="small">
              Abbrechen
            </Button>
            <Button
              onClick={handleSubmit}
              variant="contained"
              disabled={!report.trim() || isLoading}
              startIcon={isLoading ? <CircularProgress size={16} /> : undefined}
              size="small">
              {isLoading ? 'Wird gesendet...' : 'Melden'}
            </Button>
          </Box>
        </>
      )}
    </>
  );

  if (presentation === 'dialog') {
    return (
      <Dialog
        open={open}
        onClose={handleClose}
        slotProps={{
          transition: {
            onExited: handleExited,
          },
        }}
        fullWidth
        maxWidth="xs">
        <DialogContent sx={{ p: 2 }}>{content}</DialogContent>
      </Dialog>
    );
  }

  const isBelow = presentation === 'popover-below';

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={handleClose}
      slotProps={{
        transition: {
          onExited: handleExited,
        },
      }}
      anchorOrigin={
        isBelow
          ? { vertical: 'bottom', horizontal: 'left' }
          : { vertical: 'top', horizontal: 'right' }
      }
      transformOrigin={
        isBelow
          ? { vertical: 'top', horizontal: 'left' }
          : { vertical: 'bottom', horizontal: 'right' }
      }
      PaperProps={{
        sx: {
          width: 320,
          maxWidth: '90vw',
          ...(isBelow ? { mt: 0.5 } : { mt: -2 }),
          borderRadius: 1,
          boxShadow: 7,
          border: '1px solid',
          borderColor: 'divider',
        },
      }}>
      <Box sx={{ p: 2 }}>{content}</Box>
    </Popover>
  );
}
